import { Component, type ErrorInfo, type ReactNode } from "react";

import Sentry from "@snapchat/sentry-browser";
import { Button } from "@snapchat/snap-design-system";

import errorSvg from "@app-assets/svg/error.svg";

import { Layout, PageSection, TopNavigation } from "../layout/components";
import { getSentryCaptureContext, ignoredErrors, unfixableErrors } from "../sentry";

import { Box, ButtonWrapper, Content, ErrorImage, ErrorText, actionButtonStyle } from "./ErrorBoundary.styles";

export enum ProjectId {
    MY_LENSES = "my-lenses",
    WEB_APIS = "web-apis",
}

interface ErrorBoundaryProps {
    children: ReactNode;
    projectId: ProjectId;
    hideTopNav?: boolean;
}

interface State {
    hasError: boolean;
    errorIsFixable: boolean;
}

const defaultState: Readonly<State> = {
    hasError: false,
    errorIsFixable: true,
};

// Class-based component because hooks don't support `componentDidCatch` or `getDerivedStateFromError`
// https://reactjs.org/docs/hooks-faq.html#do-hooks-cover-all-use-cases-for-classes
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
    override state: State = defaultState;

    static regexArrTest(regexArr: RegExp[], string: string): boolean {
        return regexArr.some((regex) => regex.test(string));
    }

    static getDerivedStateFromError(error: Error): State {
        if (ErrorBoundary.regexArrTest(ignoredErrors, error.name)) {
            return defaultState;
        }

        // Update state so the next render will show the fallback UI.
        return {
            hasError: true,
            errorIsFixable: !ErrorBoundary.regexArrTest(unfixableErrors, error.name),
        };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error("Uncaught error:", error, errorInfo);
        Sentry.captureException(
            error,
            getSentryCaptureContext(error, {
                project: this.props.projectId,
            }),
        );
    }

    goBack = (): void => {
        window.history.back();
    };

    reload = (): void => {
        window.location.reload();
    };

    override render(): ReactNode {
        if (this.state.hasError) {
            return (
                <Layout>
                    {!this.props.hideTopNav && <TopNavigation />}
                    <Content>
                        <PageSection>
                            <Box>
                                <ErrorImage src={errorSvg} />
                                <ErrorText>Something went wrong.</ErrorText>
                                {this.state.errorIsFixable && (
                                    <ButtonWrapper>
                                        <Button className={actionButtonStyle} onClick={this.goBack}>
                                            Back
                                        </Button>
                                        <Button className={actionButtonStyle} type={"primary"} onClick={this.reload}>
                                            Reload
                                        </Button>
                                    </ButtonWrapper>
                                )}
                            </Box>
                        </PageSection>
                    </Content>
                </Layout>
            );
        }

        return this.props.children;
    }
}
